import { Fragment, useState } from 'react';
import intl from 'react-intl-universal';
import { Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { ThemeProvider } from 'styled-components';

import Lists from 'views/Kanban/Kenshin/KenshinLists';
import Header from 'views/Kanban/components/Header';
import Filters from 'views/Kanban/components/Filters';
import AlertWindow from 'views/Kanban/components/AlertPopupWindow';
import ConfirmPopUpWindow from 'views/Kanban/components/ConfirmPopUpWindow';
import { StyledConfirmModal } from 'shared/modals/Styles';

import { useMaterialUIController } from 'context';
import theme from 'assets/theme';
import themeDark from 'assets/theme-dark';

import useApi from 'shared/hooks/api';
import useMergeState from 'shared/hooks/mergeState';
import { PageLoader, PageError, RefreshButton } from 'components';
import localData from 'utils/localData';
import { useStyles } from './styles';
import { updateArrayItemById } from 'utils/javascript';

// components and parts
import MDBox from 'components/MDBox';
import { USER_COMMENT } from 'shared/constants/comment';
import { USER_DATA } from 'shared/constants/users';
import { KENSHIN_API } from 'shared/constants/apis';
import { isCurrentUserTaskManagerKenshin } from 'utils/privilege';
import { modalWidths } from 'utils/styles';
import {
  IssueStatusKenshin,
  IssueStatusToKey,
} from 'shared/constants/kenshin/issues';
import api from 'utils/api';
import { Outlet } from 'react-router-dom';

const DashboardKanbanKenshin = () => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const classes = useStyles();
  const isTaskManager = isCurrentUserTaskManagerKenshin();

  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [alertModalInfo, setAlertModalInfo] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalUpdateData, setConfirmModalUpdateData] = useState({
    issueId: '',
    destination: {
      droppableId: '',
      index: 0,
    },
    source: {
      droppableId: '',
      index: 0,
    },
    listPosition: 0,
  });
  //! TODO The type of the variables changes depending on the moment
  const [confirmModalInfo, setConfirmModalInfo] = useState({
    issueId: '',
    destination: '',
    source: '',
    listPosition: 0,
    userIds: [],
    users: [],
    CasesComplete: false,
  });

  const [filters, mergeFilters] = useMergeState({
    searchTerm: '',
    userIds: [],
    srcOrgNames: [],
    myOnly: false,
    recent: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  sevenDaysAgo.setHours(0, 0, 0, 0);

  const currentUsername = localData.get(USER_DATA.USERNAME);
  const userId = localData.get(USER_COMMENT.USER_ID);

  const [{ data, error, setLocalData }, fetchProject] = useApi.useGet(
    KENSHIN_API.PROJECT,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        dateThres: sevenDaysAgo,
        username: currentUsername,
        userId: userId,
      },
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
    }
  );

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { project } = data;

  const updateLocalProjectIssues = (issueId, updateFields) => {
    setLocalData((currentData) => ({
      project: {
        ...currentData.project,
        issues: updateArrayItemById(
          currentData.project.issues,
          issueId,
          updateFields
        ),
      },
    }));
  };

  const updateLocalProjectWithMoreDoneIssues = (updatedIssues) => {
    setLocalData((currentData) => ({
      project: {
        ...currentData.project,
        issues: updatedIssues,
      },
    }));
  };

  const handleRefreshClick = async () => {
    setIsRefreshing(true);
    await fetchProject();
    setIsRefreshing(false);
  };

  // The confirmModal Info returns a string for destination
  const confirmMessage = () => {
    if (confirmModalInfo.destination === IssueStatusKenshin.DOCTORASSIGNED) {
      return intl.get('kanban_confirm_popup_window_text_doctor_assigned');
    } else if (
      confirmModalInfo.destination === IssueStatusKenshin.REPORTREADY
    ) {
      return intl.get('kanban_confirm_popup_window_text_report_ready');
    } else if (confirmModalInfo.destination === IssueStatusKenshin.SENDBACK) {
      if (confirmModalInfo.CasesComplete) {
        return intl.get('kanban_confirm_popup_window_text_send_back');
      } else {
        // warn that there are incomplete cases;
        return (
          'まだ医師に割り当てられていない、もしくは診断されていない画像があります。' +
          '\n' +
          '\n' +
          'この操作は元に戾せません。続行してもよろしいですか？'
        );
      }
    } else if (
      confirmModalInfo.source === IssueStatusKenshin.DOCTORASSIGNED &&
      confirmModalInfo.destination === IssueStatusKenshin.NEWTASK
    ) {
      return (
        '以下の変更を行いますか？' +
        '\n' +
        '\n' +
        intl.get(IssueStatusToKey[confirmModalInfo.destination]) +
        '\n' +
        '\n' +
        '変更がある場合は、確認が必要な状態になります。'
      );
    }
    return '';
  };

  const updateIssue = () => {
    const { destination, listPosition, issueId } = confirmModalUpdateData;

    api.optimisticUpdate(
      `${KENSHIN_API.ISSUES}/single/${issueId}`,
      {
        updatedFields: {
          progress: destination.droppableId,
          listPosition: listPosition,
        },
        currentFields: project.issues.find(({ id }) => id === issueId),
        setLocalData: (fields) => updateLocalProjectIssues(issueId, fields),
      },
      {
        params: {
          organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
          username: localData.get(USER_DATA.USERNAME),
          userId: localData.get(USER_COMMENT.USER_ID),
        },
        baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      }
    );
  };

  return isTaskManager === true ? (
    <Fragment>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <MDBox data-testid="kanban-box">
          <Stack direction="row" spacing={2}>
            <Header type="kenshin" organizationName={project.name} />
            <RefreshButton
              darkMode={darkMode}
              tooltipTitle={intl.get('dashboard_refresh_button_tooltip_title')}
              handleRefreshClick={handleRefreshClick}
              isRefreshing={isRefreshing}
            />
          </Stack>

          <Grid container>
            <Grid size={12}>
              <Filters
                sourceOrganizations={project.contractsAsDstOrg}
                doctors={project.doctors}
                filters={filters}
                mergeFilters={mergeFilters}
              />
            </Grid>

            <Grid size={2} container justifyContent="flex-end"></Grid>
          </Grid>

          <Lists
            project={project}
            fetchProject={fetchProject}
            updateLocalProjectIssues={updateLocalProjectIssues}
            setAlertModalOpen={setAlertModalOpen}
            setAlertModalInfo={setAlertModalInfo}
            setConfirmModalInfo={setConfirmModalInfo}
            setConfirmModalOpen={setConfirmModalOpen}
            setConfirmModalUpdateData={setConfirmModalUpdateData}
            updateLocalProjectWithMoreDoneIssues={
              updateLocalProjectWithMoreDoneIssues
            }
          />

          <Outlet
            context={{
              doctors: project.doctors,
              updateLocalProjectIssues,
            }}
          />

          {alertModalOpen && (
            <StyledConfirmModal
              isOpen
              testid="modal:alert"
              width={modalWidths.sm}
              withCloseIcon={false}
              onClose={() => {
                setAlertModalOpen(false);
              }}
              renderContent={(modal) => (
                <AlertWindow
                  modalClose={modal.close}
                  alertModalInfo={alertModalInfo}
                />
              )}
            />
          )}

          {confirmModalOpen && (
            <StyledConfirmModal
              isOpen
              testid="kanban-kenshin-confirm-modal"
              width={modalWidths.sm}
              withCloseIcon={false}
              onClose={() => {
                setConfirmModalOpen(false);
              }}
              renderContent={(modal) => (
                <ConfirmPopUpWindow
                  project={project}
                  modalClose={modal.close}
                  updateIssue={updateIssue}
                  confirmMessage={confirmMessage()}
                />
              )}
            />
          )}
        </MDBox>
      </ThemeProvider>
    </Fragment>
  ) : (
    <Fragment>
      <Paper className={classes.paper} elevation={10}>
        <Typography variant="h4" gutterBottom>
          {intl.get('kenshin_kanban_not_allowed_message')}
        </Typography>
        <br />
        <br />
      </Paper>
    </Fragment>
  );
};

export default DashboardKanbanKenshin;
